import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import ProductSwiper from "@components/swiper/ProductSwiper";
import { useTheme } from "@emotion/react";
import ColorBall from "@components/item/ColorBall";
import { useMutation } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { useNavigate, useParams } from "react-router-dom";
import useUserInfoStore from "@store/useUserInfoStore";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import { queryClient } from "@libs/reactQuery/queryClient";
import { Flex } from "antd";
import BasicButton from "@components/button/BasicButton";
import BasicDrawer from "@components/drawer/BasicDrawer";
import { formatDate } from "@utils/functions";
import Image from "@components/item/Image";

type Props = {
  productInfo: any;
};

const SaleTemp = (props: Props) => {
  const theme = useTheme();
  const [selectedColor, setSelectedColor] = useState(null);
  const { productInfo } = props;
  const [deleteModal, setDeleteModal] = useState(false);
  const navigate = useNavigate();
  const { userInfo } = useUserInfoStore();
  const [openDrawer, setOpenDrawer] = useState(false);
  const { id, status } = useParams();
  const { deleteProductTemp } = useApiRequest();

  const selectedColorItem = productInfo?.color_list?.find(
    (item: any) => item.color_id === selectedColor,
  );

  useEffect(() => {
    setSelectedColor(productInfo?.color_list[0]?.color_id);
  }, [productInfo]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isUserProduct = userInfo?.id === productInfo?.seller_id;

  const { mutate: deleteProductTempMutate } = useMutation({
    mutationFn: () => deleteProductTemp(Number(productInfo.product_id)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getProductInventorySimple"],
      });
      navigate("/capture-list", {
        replace: true,
      });
    },
  });

  const handleMenuClick = () => {
    setOpenDrawer(true);
  };

  // 상품 설명 길이에 따라 늘어나는 textarea
  const AutoExpandTextarea = ({ value }: { value: string }) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }, [value]);

    return (
      <textarea
        ref={textareaRef}
        disabled
        value={value}
        style={{
          width: "100%",
          minHeight: "100px", // 최소 높이 유지
          resize: "none",
          overflow: "hidden",
          whiteSpace: "pre-wrap",
        }}
      />
    );
  };

  return (
    <Container>
      <EditButtonContainer>
        {isUserProduct && (
          <>
            <EditMenuButton>
              <StyledImage
                onClick={handleMenuClick}
                src={theme.image.dot_icon.default}
              />
            </EditMenuButton>
          </>
        )}
        <ProductSwiper imageList={productInfo?.product_image_list} />
      </EditButtonContainer>

      <BasicDrawer
        open={openDrawer}
        setOpen={setOpenDrawer}
        wrapperPadding="0"
        padding="0">
        <ItemBox>
          <Item onClick={() => setDeleteModal(true)}>{"삭제하기"}</Item>
        </ItemBox>
      </BasicDrawer>

      <ProductInfoWrapper>
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: "2rem" }}>
          <Data>{formatDate(productInfo?.updated_at)}</Data>
          <Flex align="center" gap={"0.5rem"}>
            <Data>임시저장</Data>
            <Image
              src={theme.image.danger_circle_icon.default}
              width="2rem"
              height="2rem"
            />
          </Flex>
        </Flex>

        <CategoryBox>
          <span>
            {productInfo?.category_ko ? productInfo?.category_ko : "-"}
          </span>
        </CategoryBox>

        <BrandWithTitle>
          <div className="brand_name">
            {productInfo?.brand_name_ko ? productInfo?.brand_name_ko : "-"}
          </div>

          <span className="title ellipsis-1">
            {productInfo?.product_name_ko ? productInfo?.product_name_ko : "-"}
          </span>
        </BrandWithTitle>

        <ConditionBox>
          <span className="label">{"컬러"}</span>
          <div className="color_box">
            {productInfo?.color_list?.map((item: any) => (
              <ColorBall
                color={item.hex_code}
                colorName={item.name}
                key={item.color_id}
                onClick={() => setSelectedColor(item.color_id)}
                active={item.color_id === selectedColor}
              />
            ))}
          </div>
        </ConditionBox>

        {selectedColorItem && (
          <ConditionBox>
            <span className="label">사이즈(수량)</span>

            <div className="color_box">
              {selectedColorItem?.size_list?.map((size: any) => (
                <SizeItem
                  className="size"
                  key={size.id}
                  disabled={size.amount === 0}>
                  {size.name} ({size.amount})
                </SizeItem>
              ))}
            </div>
          </ConditionBox>
        )}

        <PriceBox>
          <span>
            {productInfo?.price ? productInfo?.price.toLocaleString() : "-"}원
          </span>
          <span
            className="discount"
            style={{ display: "flex", gap: "0.6rem", alignItems: "flex-end" }}>
            <span>
              {"예상 판매가"} {productInfo?.price ? productInfo?.price.toLocaleString() : "-"}원
            </span>
          </span>
        </PriceBox>
      </ProductInfoWrapper>

      <Line />

      <LabelWithContent>
        <label>{"배송비"}</label>

        <Flex justify="space-between">
          <Delivery> {"기본 배송비 (도서산간 추가비용)"}</Delivery>
          <span className="value">
            {productInfo?.shipping_fee
              ? productInfo.shipping_fee.toLocaleString()
              : "-"}
            원 (+
            {4000}원)
          </span>
        </Flex>

        <DeliveryFeeBox>
          <Delivery> 50,000원 이상 구매 시</Delivery>
          <span>
            {productInfo?.free_shipping_fee === 50000 ? (
              <span>무료배송</span>
            ) : (
              ""
            )}
          </span>
        </DeliveryFeeBox>

        <Line />
      </LabelWithContent>

      <LabelWithContent>
        <label>{"상품설명"}</label>

        <ProductDescription>
          <ProductDescription>
            <AutoExpandTextarea
              value={
                productInfo?.description_ko ? productInfo?.description_ko : "-"
              }
            />
          </ProductDescription>
        </ProductDescription>
      </LabelWithContent>

      <ButtonWrapper>
        <BasicButton
          title="샤카라카에 상품 등록"
          width="100%"
          onClick={() =>
            navigate(`/register/${id}/${status}`, {
              state: {
                origin: "sale-temp",
                returnTo: `/product-detail/${id}/${status}`,
              },
            })
          }
        />
      </ButtonWrapper>

      <BasicModal
        open={deleteModal}
        setOpen={setDeleteModal}
        warning
        activeEvent={() => {
          setDeleteModal(false);
          deleteProductTempMutate();
        }}
        activeName="삭제"
        closeName="취소"
        padding="6rem 2rem 2rem 2rem">
        <ModalContent mainMessage="이 상품을 삭제하시겠습니까?" />
      </BasicModal>
    </Container>
  );
};

export default SaleTemp;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
});

const ProductInfoWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  padding: "2rem",
}));

const Data = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.2rem",
}));

const CategoryBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  color: theme.color.gray,
}));

const BrandWithTitle = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "1rem",
  paddingBottom: "1rem",
  borderBottom: `1px solid ${theme.color.border_gray}`,

  ".brand_name": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.8rem",
  },

  ".brand": {
    fontSize: "2rem",
    fontWeight: theme.fontWeight.semiBold,
    lineHeight: "2rem",
  },
}));

const ConditionBox = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  color: theme.color.gray,

  ".label": {
    minWidth: "8rem",
  },

  ".color_box": {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
  },
}));

const SizeItem = styled.span<{ disabled: boolean }>(({ theme, disabled }) => ({
  padding: "0.7rem",
  color: disabled ? theme.color.light_gray : theme.color.black,
  minHeight: "3rem",
}));

const PriceBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "1rem",
  fontWeight: theme.fontWeight.bold,
  fontSize: "1.6rem",
  padding: "1rem 0",

  ".discount": {
    color: theme.color.green,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: "2rem",
  },
}));

const LabelWithContent = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  padding: "2rem 2rem 0 2rem",
  lineHeight: "2rem",

  label: {
    display: "flex",
    gap: "0.8rem",
    alignItems: "center",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.semiBold,
    lineHeight: "2rem",
  },

  ".brand": {
    fontSize: "2rem",
    fontWeight: theme.fontWeight.semiBold,
    lineHeight: "2rem",
  },
}));

const Line = styled.div(({ theme }) => ({
  width: "100%",
  height: "1px",
  backgroundColor: theme.color.border_gray,
}));

const Delivery = styled.span(({ theme }) => ({
  color: theme.color.gray,
  fontWeight: theme.fontWeight.medium,
}));

const DeliveryFeeBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const ProductDescription = styled.div(({ theme }) => ({
  width: "100%",
  height: "100%",
  textarea: {
    width: "100%",
    height: "150px",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    resize: "none",

    ":disabled": {
      backgroundColor: "white",
    },
  },
}));

const EditButtonContainer = styled.div(({ theme }) => ({
  position: "relative",
  width: "100%",
}));

const EditMenuButton = styled.div(({ theme }) => ({
  position: "absolute",
  zIndex: 2,
  width: "100%",
  background: "linear-gradient(0deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)",
  height: "50px",
}));

const StyledImage = styled.img(({ theme }) => ({
  display: "flex",
  objectFit: "cover",
  position: "absolute",
  right: "1.6rem",
  top: "1rem",
  cursor: "pointer",
}));

const ItemBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
}));

const Item = styled.button(({ theme }) => ({
  width: "100%",
  minHeight: "5.6rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${theme.color.border_gray}`,

  "&:first-child": {
    border: "none",
  },

  "&:last-child": {
    color: theme.color.orange,
    justifyContent: "center",
  },
}));

const ButtonWrapper = styled.div(({ theme }) => ({
  padding: "1rem",
}));
